import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI } from '../../../api';
import Layout from '../../../components/Layout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Plus, Edit, Eye, Loader2 } from 'lucide-react';
import { useToast } from '../../../hooks/useToast';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);

export default function ChartOfAccounts() {
  const navigate = useNavigate();
  const { success, error: showError, ToastContainer } = useToast();
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    active_only: 'true',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    accountNumber: '',
    accountName: '',
    accountCategory: '',
    accountType: '',
    parentCategory: '',
    notes: '',
  });

  useEffect(() => {
    fetchAccounts();
  }, [filters]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await accountingAPI.getAccounts({
        category: filters.category || undefined,
        type: filters.type || undefined,
        active_only: filters.active_only,
      });
      setAccounts(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load chart of accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (!formData.accountNumber || !formData.accountName) {
      showError('Error', 'Account number and name are required');
      return;
    }

    if (!/^\d{4}$/.test(formData.accountNumber)) {
      showError('Error', 'Account number must be 4 digits');
      return;
    }

    try {
      setCreating(true);
      await accountingAPI.createAccount(formData);
      success('Success', 'Account created successfully');
      setShowCreateModal(false);
      setFormData({
        accountNumber: '',
        accountName: '',
        accountCategory: '',
        accountType: '',
        parentCategory: '',
        notes: '',
      });
      fetchAccounts();
    } catch (err) {
      showError('Error', err.message || 'Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (account) => {
    try {
      await accountingAPI.updateAccount(account.id, { isActive: !account.isActive });
      success('Success', `Account ${account.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchAccounts();
    } catch (err) {
      showError('Error', err.message || 'Failed to update account');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      sales: 'success',
      purchases: 'warning',
      direct_expenses: 'danger',
      overheads: 'neutral',
    };
    return colors[category] || 'neutral';
  };

  const groupedAccounts = accounts.reduce((acc, account) => {
    const category = account.accountCategory || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(account);
    return acc;
  }, {});

  return (
    <>
      <ToastContainer />
      <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Chart of Accounts
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage your nominal accounts (4000-9999)
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
              >
                ← Back to Reports
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <Select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="h-8"
                >
                  <option value="">All Categories</option>
                  <option value="sales">Sales</option>
                  <option value="purchases">Purchases</option>
                  <option value="direct_expenses">Direct Expenses</option>
                  <option value="overheads">Overheads</option>
                </Select>
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <Select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="h-8"
                >
                  <option value="">All Types</option>
                  <option value="revenue">Revenue</option>
                  <option value="expense">Expense</option>
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                  <option value="equity">Equity</option>
                </Select>
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <Select
                  value={filters.active_only}
                  onChange={(e) => setFilters({ ...filters, active_only: e.target.value })}
                  className="h-8"
                >
                  <option value="true">Active Only</option>
                  <option value="false">All Accounts</option>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={fetchAccounts}
                  className="w-full"
                >
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">
            {error}
          </div>
        )}

        {/* Accounts List */}
        <Card>
          {loading ? (
            <CardContent className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-accent-primary)]" />
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading accounts...</p>
            </CardContent>
          ) : (
            <CardContent className="p-6">
              <div className="space-y-6">
                {Object.keys(groupedAccounts).length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-xs text-[var(--ds-text-muted)]">No accounts found</p>
                  </div>
                ) : (
                  Object.entries(groupedAccounts).map(([category, categoryAccounts]) => (
                    <div key={category}>
                      <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4 capitalize">
                        {category.replace('_', ' ')}
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow isHeader>
                            <TableHead>Account #</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead align="right">Balance</TableHead>
                            <TableHead align="center">Status</TableHead>
                            <TableHead align="right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categoryAccounts.map((account) => (
                            <TableRow key={account.id}>
                              <TableCell>
                                <span className="font-mono">{account.accountNumber}</span>
                              </TableCell>
                              <TableCell>
                                {account.accountName}
                                {account.isSystemAccount && (
                                  <Badge variant="neutral" className="ml-2">System</Badge>
                                )}
                              </TableCell>
                              <TableCell>{account.accountType || '-'}</TableCell>
                              <TableCell align="right" className="font-medium">
                                {currency(account.currentBalance)}
                              </TableCell>
                              <TableCell align="center">
                                <Badge
                                  variant={account.isActive ? 'success' : 'neutral'}
                                >
                                  {account.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                              <TableCell align="right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={() => navigate(`/tenant/reports/accounts/${account.id}`)}
                                    title="View Entries"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {!account.isSystemAccount && (
                                    <Button
                                      variant="outline"
                                      size="icon-sm"
                                      onClick={() => handleToggleActive(account)}
                                      title={account.isActive ? 'Deactivate' : 'Activate'}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Create Account Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create New Account</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Number <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                    placeholder="4000-9999"
                    maxLength={4}
                    required
                    className="h-8"
                  />
                  <p className="mt-1 text-xs text-[var(--ds-text-muted)]">Must be 4 digits</p>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Account Name <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.accountName}
                    onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                    required
                    className="h-8"
                  />
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <Select
                    value={formData.accountCategory}
                    onChange={(e) => setFormData({ ...formData, accountCategory: e.target.value })}
                    className="h-8"
                  >
                    <option value="">Select category</option>
                    <option value="sales">Sales</option>
                    <option value="purchases">Purchases</option>
                    <option value="direct_expenses">Direct Expenses</option>
                    <option value="overheads">Overheads</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <Select
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                    className="h-8"
                  >
                    <option value="">Select type</option>
                    <option value="revenue">Revenue</option>
                    <option value="expense">Expense</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                    <option value="equity">Equity</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Parent Category
                  </label>
                  <Input
                    type="text"
                    value={formData.parentCategory}
                    onChange={(e) => setFormData({ ...formData, parentCategory: e.target.value })}
                    placeholder="e.g., Premises & Utilities"
                    className="h-8"
                  />
                </div>
                <div>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <Input
                    type="text"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="h-8"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={creating}
                    className="flex-1"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
    </>
  );
}
